// The Payments due board: every open bill in one section (overdue, due this week, deposits waiting, scheduled in Wise,
// later, to approve), POs whose deposit isn't billed yet, and what was paid in the last 30 days, each section with its
// total (shared/bills.ts paymentsDue()). People who may pay tick bills and pay them through Wise (HHL Ops makes the
// transfers; they're approved and funded in the Wise app) or record a payment made outside. Both lists are the app's
// usual table (DataTable: views, columns, resizing, Copy / CSV): the open bills banded by section, each band with its
// total; what was paid with a totals row.
import { AlertTriangle, CheckCircle2, ExternalLink, Plus } from "lucide-react";
import { type ReactNode, type SyntheticEvent, useMemo, useState } from "react";
import {
  BILL_KIND_LABELS,
  type BillIndex,
  billMatch,
  DUE_SECTION_HINTS,
  DUE_SECTION_LABELS,
  type DueRow,
  type DueSection,
  MATCH_LABELS,
  MATCH_TONE,
  PAYMENT_STATUS_LABELS,
  PAYMENT_STATUS_TONE,
  paymentsDue,
  paymentWith,
  poTotal,
  vendorLabelOf,
} from "../../../../shared/bills.ts";
import type { Bill, Payment } from "../../../../shared/db.gen.ts";
import { sumMoney } from "../../../../shared/po.ts";
import { amount, dayOf, totalsText, useBillIndex, useCanPay, wiseFeature } from "../../lib/bills.ts";
import { shortDate } from "../../lib/costs.ts";
import { plural } from "../../lib/format.ts";
import { can, mutate, mutateWithResult } from "../../lib/mutate.ts";
import { setSearch } from "../../lib/router.tsx";
import { Badge, Button, cx } from "../../ui/controls.tsx";
import { CellControls, type Column, DataTable, type TableSection } from "../../ui/DataTable.tsx";
import { openPane, PaneLink } from "../../ui/SidePane.tsx";
import { toast } from "../../ui/toast.tsx";
import { Why } from "../../ui/Why.tsx";
import { PaidOutsideDialog, PayDialog } from "./PayDialogs.tsx";

const ORDER: DueSection[] = ["overdue", "this_week", "deposits", "scheduled", "later", "drafts"];
const TONE: Record<DueSection, string> = {
  overdue: "text-danger",
  this_week: "text-warn",
  deposits: "text-accent-text",
  scheduled: "text-accent-text",
  later: "text-text",
  drafts: "text-muted",
};

type Row = DueRow & { id: string; vendor: string; runs: string; match: ReturnType<typeof billMatch> | null };
type PaidRow = { id: string; payment: Payment; bills: Bill[]; vendor: string; how: string };

/** Totals by currency over some rows, as paymentsDue() adds them: "$1,200.00 + 2,100.00 CNY". */
const sumText = (rows: { amount: number; currency: string }[]) => {
  const other: Record<string, number> = {};
  let usd = 0;
  for (const r of rows)
    if (r.currency.toUpperCase() === "USD") usd = sumMoney([usd, r.amount]);
    else other[r.currency] = sumMoney([other[r.currency] ?? 0, r.amount]);
  return totalsText({ usd, other });
};

export function DueBoard({ vendorId, runId }: { vendorId: string | null; runId: string | null }) {
  const ix = useBillIndex();
  const board = useMemo(() => paymentsDue(ix, { dayOf, vendorId, runId }), [ix, vendorId, runId]);
  const canPay = useCanPay();
  const [picked, setPicked] = useState<Set<string>>(() => new Set());
  const [dialog, setDialog] = useState<"pay" | "outside" | null>(null);
  // Table views --- each table's View and Columns menus, by its title
  const [dueSlot, setDueSlot] = useState<HTMLDivElement | null>(null);
  const [paidSlot, setPaidSlot] = useState<HTMLDivElement | null>(null);
  // Bills that can be paid now: approved, something left, not already on its way.
  const payable = (r: DueRow) => !!r.bill && r.bill.status === "approved" && r.amount > 0;
  const rows = useMemo<Row[]>(
    () =>
      ORDER.flatMap((s) => board.sections[s]).map((r) => ({
        ...r,
        id: r.key,
        vendor: vendorLabelOf(ix.vendorById.get(r.vendorId)),
        runs: r.runIds
          .map((id) => ix.runById.get(id)?.number)
          .filter(Boolean)
          .join(", "),
        match: r.bill ? billMatch(ix, r.bill.id) : null,
      })),
    [board, ix],
  );
  const paid = useMemo<PaidRow[]>(
    () =>
      board.recentlyPaid.map(({ payment: p, bills }) => ({
        id: p.id,
        payment: p,
        bills,
        vendor: vendorLabelOf(ix.vendorById.get(p.vendor_id ?? "")),
        how: paymentWith(ix, p),
      })),
    [board, ix],
  );
  // Picking --- rows are picked by their key; only bills that can be paid now can be
  const canPick = new Set(rows.filter((r) => canPay && payable(r)).map((r) => r.id));
  const chosen = rows.filter((r) => picked.has(r.id) && canPick.has(r.id));
  const billIds = chosen.map((r) => r.bill?.id as string);
  const nothing = !rows.length && !paid.length;
  const columns = useMemo(() => dueColumns(ix, canPay), [ix, canPay]);
  const section = (r: Row): TableSection => ({
    key: r.section,
    order: ORDER.indexOf(r.section),
    label: (
      // Kept inside the table's width (the band's count follows it)
      <span className="flex max-w-[calc(100cqw-5rem)] min-w-0 items-end gap-2">
        <span className={cx("shrink-0", TONE[r.section])}>{DUE_SECTION_LABELS[r.section]}</span>
        <span className="shrink-0 font-mono text-text normal-case">{totalsText(board.totals[r.section])}</span>
        <span className="truncate font-normal text-faint normal-case" title={DUE_SECTION_HINTS[r.section]}>
          {DUE_SECTION_HINTS[r.section]}
        </span>
      </span>
    ),
  });

  return (
    <div className="mx-auto max-w-6xl px-4 py-5 sm:px-6">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {(["overdue", "this_week", "deposits", "scheduled"] as const).map((s) => (
          <a key={s} href="#due-bills" className="rounded-lg border border-line bg-surface px-4 py-3 hover:border-line-strong">
            <div className="text-[11px] font-medium tracking-wide text-faint uppercase">{DUE_SECTION_LABELS[s]}</div>
            <div className={cx("mt-1 font-mono text-lg font-semibold", board.totals[s].count ? TONE[s] : "text-faint")}>{totalsText(board.totals[s])}</div>
            <div className="mt-0.5 text-[12px] text-muted">
              {board.totals[s].count} {board.totals[s].count === 1 ? "item" : "items"}
            </div>
          </a>
        ))}
      </div>
      {nothing && (
        <div className="mt-6 rounded-lg border border-dashed border-line-strong bg-surface p-8 text-center text-muted">
          Nothing is due. Bills come from a vendor's invoice (New bill: drop the PDF or photo), or from a PO's Bills tab (its deposit and balance).
        </div>
      )}
      {rows.length > 0 && (
        <section id="due-bills" className="mt-6 scroll-mt-4">
          <div className="mb-1.5 flex items-center gap-2">
            <h2 className="text-[13px] font-semibold">Payments due</h2>
            {!canPay && (
              <span className="min-w-0 text-[12px] text-muted">
                Only people allowed to make payments can pay or mark bills paid (Mitch and Jeff; an admin turns on Can pay in Settings → Users).
              </span>
            )}
            <div ref={setDueSlot} className="ml-auto flex shrink-0 items-center gap-1" />
          </div>
          <div className="rounded-md bg-surface">
            <DataTable
              fit={20}
              rows={rows}
              columns={columns}
              sectionOf={section}
              defaultSort={{ key: "due" }}
              onOpen={(r) => (r.bill ? setSearch({ bill: r.bill.id }) : r.po && openPane({ kind: "po", id: r.po.id }, { root: true }))}
              selection={canPay ? { selected: picked, onChange: setPicked } : undefined}
              selectable={(r) => canPick.has(r.id)} // only bills that can be paid now take a tick
              exportName="Payments due"
              viewsSlot={dueSlot}
              pageFilters={false}
              rowNoun={["item", "items"]}
            />
          </div>
        </section>
      )}
      {paid.length > 0 && (
        <section className="mt-6">
          <div className="mb-1.5 flex items-baseline gap-2">
            <h2 className="text-[13px] font-semibold text-ok">Recently paid</h2>
            <span className="text-[12px] text-muted">Payments made in the last 30 days</span>
            <div ref={setPaidSlot} className="ml-auto flex items-center gap-1 self-center" />
          </div>
          <div className="rounded-md bg-surface">
            <DataTable
              fit
              rows={paid}
              columns={paidColumns}
              defaultSort={{ key: "paid_on", desc: true }}
              onOpen={(r) => r.bills[0] && setSearch({ bill: r.bills[0].id })}
              exportName="Recently paid"
              viewsSlot={paidSlot}
              pageFilters={false}
              rowNoun={["payment", "payments"]}
            />
          </div>
        </section>
      )}
      {chosen.length > 0 && (
        <div className="sticky bottom-3 mt-4 flex items-center gap-3 rounded-lg border border-accent/40 bg-surface px-4 py-2.5 shadow-pop">
          <span className="font-medium">
            {chosen.length} {chosen.length === 1 ? "bill" : "bills"} ·{" "}
            <span className="font-mono">{totalsText({ usd: chosen.filter((r) => r.currency === "USD").reduce((n, r) => n + r.amount, 0), other: {} })}</span>
          </span>
          <button type="button" className="text-[12px] text-muted hover:underline" onClick={() => setPicked(new Set())}>
            Clear
          </button>
          <div className="ml-auto flex gap-2">
            <Button onClick={() => setDialog("outside")}>Mark paid outside…</Button>
            <Button
              variant="primary"
              onClick={() => setDialog("pay")}
              disabled={!wiseFeature().enabled}
              title={wiseFeature().enabled ? "" : "Wise isn't connected: Settings → Wise"}
            >
              Pay with Wise…
            </Button>
          </div>
        </div>
      )}
      {dialog === "pay" && (
        <PayDialog
          billIds={billIds}
          onClose={(done) => {
            setDialog(null);
            if (done) setPicked(new Set());
          }}
        />
      )}
      {dialog === "outside" && (
        <PaidOutsideDialog
          billIds={billIds}
          onClose={(done) => {
            setDialog(null);
            if (done) setPicked(new Set());
          }}
        />
      )}
    </div>
  );
}

function dueColumns(ix: BillIndex, canPay: boolean): Column<Row>[] {
  const createDeposit = async (r: Row) => {
    if (!r.po) return;
    const id = crypto.randomUUID();
    const { ok } = await mutateWithResult("bill.create_from_po", { id, po_id: r.po.id, part: "deposit", approve: true });
    if (ok) {
      toast.ok(`Deposit bill made for ${r.po.number}: attach the vendor's invoice to it`);
      setSearch({ bill: id });
    }
  };
  return [
    {
      key: "due",
      header: "Due",
      width: "140px",
      value: (r) => r.due ?? "",
      text: (r) => (r.due ? `${r.due}${r.overdueDays > 0 ? ` (${plural(r.overdueDays, "day")} late)` : ""}` : "No date"),
      cell: (r) =>
        r.due ? (
          <Why title="When it's due" note={r.dueWhy} className="truncate text-[12px]">
            <span className={cx(r.overdueDays ? "font-medium text-danger" : "text-muted")}>{shortDate(r.due)}</span>
            {r.overdueDays > 0 && <span className="text-[11px] text-danger"> · {plural(r.overdueDays, "day")} late</span>}
          </Why>
        ) : (
          <span className="text-[12px] text-faint" title={r.dueWhy}>
            No date
          </span>
        ),
    },
    { key: "vendor", header: "Vendor", width: "minmax(120px,1fr)", cell: (r) => <span className="truncate font-medium">{r.vendor}</span> },
    {
      key: "label",
      header: "Bill",
      width: "minmax(140px,1.3fr)",
      cell: (r) =>
        r.bill ? (
          <PaneLink
            kind="bill"
            id={r.bill.id}
            to={`/bills?tab=bills&status=all&bill=${r.bill.id}`}
            className="truncate font-mono text-[12px] text-accent-text hover:underline"
          >
            {r.label}
          </PaneLink>
        ) : (
          <PaneLink kind="po" id={r.po?.id ?? ""} to={`/pos?open=${r.po?.id}`} className="truncate text-[12px] text-accent-text hover:underline">
            {r.label}
          </PaneLink>
        ),
    },
    {
      key: "kind",
      header: "Kind",
      width: "90px",
      value: (r) => (r.bill ? BILL_KIND_LABELS[r.bill.kind] : "Deposit"),
      cell: (r) => <span className="truncate text-[12px] text-muted">{r.bill ? BILL_KIND_LABELS[r.bill.kind] : "Deposit"}</span>,
    },
    {
      key: "description",
      header: "Description",
      width: "minmax(120px,1fr)",
      optional: true,
      value: (r) => r.bill?.description ?? "",
      cell: (r) => (
        <span className="truncate text-[12px] text-muted" title={r.bill?.description}>
          {r.bill?.description}
        </span>
      ),
    },
    { key: "runs", header: "Run", width: "80px", cell: (r) => <span className="truncate text-[12px] text-muted">{r.runs}</span> },
    {
      key: "match",
      header: "Match",
      width: "120px",
      value: (r) => (r.match && r.match.status !== "nothing" ? MATCH_LABELS[r.match.status] : ""),
      cell: (r) => r.match && r.match.status !== "nothing" && <Badge tone={MATCH_TONE[r.match.status]}>{MATCH_LABELS[r.match.status]}</Badge>,
    },
    {
      key: "amount",
      header: "Amount",
      width: "120px",
      align: "right",
      value: (r) => r.amount,
      text: (r) => amount(r.amount, r.currency),
      cell: (r) => (
        <span className="font-mono font-medium">
          <Why
            title={r.label}
            lines={
              r.bill
                ? [
                    { label: "Bill amount", value: amount(Number(r.bill.amount), r.currency) },
                    ...(r.section === "scheduled" ? [{ label: "On its way in Wise", value: amount(r.amount, r.currency) }] : []),
                    {
                      label: r.section === "scheduled" ? "Paid so far" : "Still to pay",
                      value: amount(r.section === "scheduled" ? Number(r.bill.amount) - r.amount : r.amount, r.currency),
                    },
                  ]
                : [{ label: `${r.po?.deposit_pct}% of ${r.po?.number}`, value: amount(r.amount, r.currency) }]
            }
            note={r.dueWhy}
          >
            {amount(r.amount, r.currency)}
          </Why>
        </span>
      ),
    },
    {
      key: "payments",
      header: "In Wise",
      width: "minmax(120px,1fr)",
      value: (r) => r.payments.map((p) => PAYMENT_STATUS_LABELS[p.status]).join(", "),
      cell: (r) => (
        <CellControls className="gap-2 text-[12px]">
          {r.payments.map((p) => (
            <span key={p.id} className="flex min-w-0 items-center gap-1.5">
              <Badge tone={PAYMENT_STATUS_TONE[p.status]}>{PAYMENT_STATUS_LABELS[p.status]}</Badge>
              {p.wise_transfer_id ? (
                <a
                  href={wiseLink(p.wise_env || wiseFeature().env, Number(p.wise_transfer_id))}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1 whitespace-nowrap text-accent-text hover:underline"
                  title="Approve and fund it in the Wise app"
                >
                  Open in Wise <ExternalLink className="size-3" />
                </a>
              ) : (
                p.wise_error && (
                  <span title={p.wise_error}>
                    <AlertTriangle className="size-3.5 text-warn" aria-label={p.wise_error} />
                  </span>
                )
              )}
            </span>
          ))}
        </CellControls>
      ),
    },
    {
      key: "actions",
      header: "",
      width: "140px",
      value: () => null,
      cell: (r) => (
        <CellControls className="ml-auto gap-2 text-[12px]">
          {r.po &&
            can("bill.create_from_po") &&
            // A PO with a line without a price can't be billed (billFromPo refuses it): its prices come first.
            (poTotal(ix, r.po.id).unpriced ? (
              <span className="truncate text-warn" title={`${r.po.number} has lines without a price`}>
                Set its prices first
              </span>
            ) : (
              <Button size="sm" icon={<Plus className="size-3.5" />} onClick={() => createDeposit(r)}>
                Bill the deposit
              </Button>
            ))}
          {r.bill?.status === "draft" && can("bill.set_status") && (
            <Button
              size="sm"
              icon={<CheckCircle2 className="size-3.5" />}
              onClick={() => mutate("bill.set_status", { id: r.bill?.id as string, status: "approved" })}
            >
              Approve
            </Button>
          )}
          {!canPay && r.bill?.status === "approved" && <span className="text-faint">Approved</span>}
        </CellControls>
      ),
    },
  ];
}

const paidColumns: Column<PaidRow>[] = [
  {
    key: "paid_on",
    header: "Paid on",
    width: "100px",
    value: (r) => String(r.payment.paid_on ?? ""),
    cell: (r) => <span className="text-[12px] text-muted">{shortDate(r.payment.paid_on)}</span>,
  },
  { key: "vendor", header: "Vendor", width: "minmax(130px,1fr)", cell: (r) => <span className="truncate font-medium">{r.vendor}</span> },
  {
    key: "number",
    header: "Payment",
    width: "96px",
    value: (r) => Number(r.payment.number.replace(/\D/g, "")) || 0,
    text: (r) => r.payment.number,
    cell: (r) => <span className="font-mono text-[12px] text-muted">{r.payment.number}</span>,
  },
  { key: "how", header: "How", width: "minmax(110px,1fr)", cell: (r) => <span className="truncate text-[12px] text-muted">{r.how}</span> },
  {
    key: "reference",
    header: "Reference",
    width: "minmax(130px,1fr)",
    value: (r) => r.payment.reference,
    cell: (r) => (
      <span className="truncate font-mono text-[12px] text-muted" title={r.payment.reference}>
        {r.payment.reference}
      </span>
    ),
  },
  {
    key: "bills",
    header: "Bills",
    width: "minmax(110px,1fr)",
    value: (r) => r.bills.map((b) => b.number).join(", "),
    cell: (r) => (
      <span className="flex min-w-0 items-center gap-2">
        {r.bills.map((b) => (
          <PaneLink
            key={b.id}
            kind="bill"
            id={b.id}
            to={`/bills?tab=bills&status=all&bill=${b.id}`}
            className="font-mono text-[12px] text-accent-text hover:underline"
          >
            {b.number}
          </PaneLink>
        ))}
      </span>
    ),
  },
  {
    key: "amount",
    header: "Amount",
    width: "120px",
    align: "right",
    value: (r) => Number(r.payment.amount),
    text: (r) => amount(Number(r.payment.amount), r.payment.currency),
    cell: (r) => <span className="font-mono">{amount(Number(r.payment.amount), r.payment.currency)}</span>,
    total: (rows) => <span className="font-mono">{sumText(rows.map((r) => ({ amount: Number(r.payment.amount), currency: r.payment.currency })))}</span>,
  },
  {
    key: "fee",
    header: "Fee",
    width: "120px",
    align: "right",
    value: (r) => Number(r.payment.fee) || 0,
    text: (r) => (Number(r.payment.fee) ? amount(Number(r.payment.fee), r.payment.fee_currency || r.payment.currency) : ""),
    cell: (r) => (
      <span className="text-[12px] text-muted">
        {Number(r.payment.fee) ? `+ ${amount(Number(r.payment.fee), r.payment.fee_currency || r.payment.currency)} fee` : ""}
      </span>
    ),
  },
];

/** Where a person approves and funds a transfer (or sees it) in Wise. */
export const wiseLink = (env: string, id: number) =>
  `${env === "live" ? "https://wise.com" : "https://sandbox.transferwise.tech"}/transactions/activities/by-resource/TRANSFER/${id}`;
